import z from 'zod';
import type {
  AddAttachmentEvent,
  AddGeotagEvent,
  AddNoteEvent,
  BaseListEvent,
  CloseListEvent,
  CopyEvent,
  CreateListEvent,
  EditGeotagEvent,
  EditNoteEvent,
  ExpandProtoEvent,
  InsertEvent,
  ListEvent,
  ModifyListEvent,
  MoveEvent,
  OpenListEvent,
  PatchEvent,
  RemoveAttachmentEvent,
  RemoveEvent,
  RemoveGeotagEvent,
  RemoveNoteEvent,
} from './Checklist.types';

// ---------------------------------------------------------------------------
// Base event fields (shared across all list events)
// ---------------------------------------------------------------------------

const zBaseListEvent = {
  id: z.uuid(),
  recnum: z.int().optional(),
  list_id: z.uuid(),
  created: z.date(),
  inserted: z.date().nullable().optional(),
  user_id: z.int(),
} as const satisfies {
  [P in keyof Omit<BaseListEvent, 'payload' | 'type'>]: z.ZodType<
    any,
    any,
    any
  >;
};

// ---------------------------------------------------------------------------
// Geotag / Position schemas
// ---------------------------------------------------------------------------

const zCoords = z.object({
  speed: z.number().nullable(),
  heading: z.number().nullable(),
  accuracy: z.number().nullable(),
  altitude: z.number().nullable(),
  altitudeAccuracy: z.number().nullable(),
  latitude: z.number(),
  longitude: z.number(),
});

const zListPosition = z.object({
  coords: zCoords,
  timestamp: z.number(),
});

const zGeotag = z.object({
  label: z.string().nullable().optional(),
  position: zListPosition,
});

// ---------------------------------------------------------------------------
// Partial field schema (for INSERT payloads)
// ---------------------------------------------------------------------------

const zPartialGroupField = z.object({
  type: z.literal('GROUP_FIELD'),
  id: z.uuid(),
  created: z.date(),
  title_en: z.string().optional(),
  title_nl: z.string().optional(),
});

const zPartialCheckpointField = z.object({
  type: z.literal('CHECKPOINT_FIELD'),
  id: z.uuid(),
  created: z.date(),
  title_en: z.string().optional(),
  title_nl: z.string().optional(),
  description_en: z.string().optional(),
  description_nl: z.string().optional(),
  value: z.boolean().nullable().optional(),
});

const zPartialNumericField = z.object({
  type: z.literal('NUMERIC_FIELD'),
  id: z.uuid(),
  created: z.date(),
  title_en: z.string().optional(),
  title_nl: z.string().optional(),
  description_en: z.string().optional(),
  description_nl: z.string().optional(),
  value: z.number().nullable().optional(),
});

const zPartialTaskField = z.object({
  type: z.literal('TASK_FIELD'),
  id: z.uuid(),
  created: z.date(),
  title_en: z.string().optional(),
  title_nl: z.string().optional(),
  description_en: z.string().optional(),
  description_nl: z.string().optional(),
  value: z.boolean().nullable().optional(),
});

const zPartialField = z.discriminatedUnion('type', [
  zPartialGroupField,
  zPartialCheckpointField,
  zPartialNumericField,
  zPartialTaskField,
]);

// ---------------------------------------------------------------------------
// Patch field schema (for PATCH payloads)
// ---------------------------------------------------------------------------

const zPatchField = z.object({
  type: z
    .enum(['CHECKPOINT_FIELD', 'NUMERIC_FIELD', 'TASK_FIELD', 'GROUP_FIELD'])
    .optional(),
  title_en: z.string().optional(),
  title_nl: z.string().optional(),
  description_en: z.string().optional(),
  description_nl: z.string().optional(),
  value: z.union([z.boolean(), z.number()]).nullable().optional(),
});

// ---------------------------------------------------------------------------
// Event schemas (prefixed with zList to avoid collisions with Ticket schemas)
// ---------------------------------------------------------------------------

export const zListCreateListEvent = z.strictObject({
  type: z.literal('CREATE_LIST'),
  payload: z.object({
    name: z.string().optional(),
    werk_id: z.int().nullable().optional(),
    associated_users: z.array(z.int()).optional(),
    tags: z.array(z.string()).optional(),
    start_date: z.date().optional(),
    end_date: z.date().optional(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<CreateListEvent>;

export const zListModifyListEvent = z.strictObject({
  type: z.literal('MODIFY_LIST'),
  payload: z.object({
    name: z.string().optional(),
    werk_id: z.int().nullable().optional(),
    associated_users: z.array(z.int()).optional(),
    tags: z.array(z.string()).optional(),
    start_date: z.date().optional(),
    end_date: z.date().optional(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<ModifyListEvent>;

export const zListInsertEvent = z.strictObject({
  type: z.literal('INSERT'),
  payload: z.object({
    node: zPartialField,
    targetNodeId: z.uuid(),
    index: z.int().optional(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<InsertEvent>;

export const zListPatchEvent = z.strictObject({
  type: z.literal('PATCH'),
  payload: z.object({
    partialNode: zPatchField,
    targetNodeId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<PatchEvent>;

export const zListRemoveEvent = z.strictObject({
  type: z.literal('REMOVE'),
  payload: z.object({
    targetNodeId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<RemoveEvent>;

export const zListMoveEvent = z.strictObject({
  type: z.literal('MOVE'),
  payload: z.object({
    targetNodeId: z.uuid(),
    subjectNodeId: z.uuid(),
    index: z.int().optional(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<MoveEvent>;

export const zListCopyEvent = z.strictObject({
  type: z.literal('COPY'),
  payload: z.object({
    targetNodeId: z.uuid(),
    subjectNodeId: z.uuid(),
    index: z.int().optional(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<CopyEvent>;

export const zListExpandProtoEvent = z.strictObject({
  type: z.literal('EXPAND_PROTO'),
  payload: z.object({
    targetNodeId: z.uuid(),
    protoListId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<ExpandProtoEvent>;

export const zListAddAttachmentEvent = z.strictObject({
  type: z.literal('ADD_ATTACHMENT'),
  payload: z.object({
    targetNodeId: z.uuid(),
    attachment: z.object({
      contentType: z.string(),
      checksum: z.string(),
      size: z.number(),
      filename: z.string().nullable(),
    }),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<AddAttachmentEvent>;

export const zListRemoveAttachmentEvent = z.strictObject({
  type: z.literal('REMOVE_ATTACHEMNT'),
  payload: z.object({
    targetNodeId: z.uuid(),
    checksum: z.string(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<RemoveAttachmentEvent>;

export const zListAddNoteEvent = z.strictObject({
  type: z.literal('ADD_NOTE'),
  payload: z.object({
    note: z.string(),
    targetNodeId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<AddNoteEvent>;

export const zListEditNoteEvent = z.strictObject({
  type: z.literal('EDIT_NOTE'),
  payload: z.object({
    text: z.string(),
    targetNodeId: z.uuid(),
    noteId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<EditNoteEvent>;

export const zListRemoveNoteEvent = z.strictObject({
  type: z.literal('REMOVE_NOTE'),
  payload: z.object({
    targetNodeId: z.uuid(),
    noteId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<RemoveNoteEvent>;

export const zListAddGeotagEvent = z.strictObject({
  type: z.literal('ADD_GEOTAG'),
  payload: z.object({
    geotag: zGeotag,
    targetNodeId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<AddGeotagEvent>;

export const zListEditGeotagEvent = z.strictObject({
  type: z.literal('EDIT_GEOTAG'),
  payload: z.object({
    geotag: z.object({
      label: z.string().nullable().optional(),
      position: zListPosition.optional(),
    }),
    targetNodeId: z.uuid(),
    geotagId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<EditGeotagEvent>;

export const zListRemoveGeotagEvent = z.strictObject({
  type: z.literal('REMOVE_GEOTAG'),
  payload: z.object({
    targetNodeId: z.uuid(),
    geotagId: z.uuid(),
  }),
  ...zBaseListEvent,
}) satisfies z.ZodType<RemoveGeotagEvent>;

export const zListCloseListEvent = z.strictObject({
  type: z.literal('CLOSE_LIST'),
  payload: z.object({}),
  ...zBaseListEvent,
}) satisfies z.ZodType<CloseListEvent>;

export const zListOpenListEvent = z.strictObject({
  type: z.literal('OPEN_LIST'),
  payload: z.object({}),
  ...zBaseListEvent,
}) satisfies z.ZodType<OpenListEvent>;

// ---------------------------------------------------------------------------
// Discriminated union of all list event types
// ---------------------------------------------------------------------------

export const zListEvent: z.ZodType<ListEvent> = z.discriminatedUnion('type', [
  zListCreateListEvent,
  zListModifyListEvent,
  zListInsertEvent,
  zListPatchEvent,
  zListRemoveEvent,
  zListMoveEvent,
  zListCopyEvent,
  zListExpandProtoEvent,
  zListAddAttachmentEvent,
  zListRemoveAttachmentEvent,
  zListAddNoteEvent,
  zListEditNoteEvent,
  zListRemoveNoteEvent,
  zListAddGeotagEvent,
  zListEditGeotagEvent,
  zListRemoveGeotagEvent,
  zListCloseListEvent,
  zListOpenListEvent,
] as const);
